import { TypographyGroupValue } from '@makyoapp/makyo-common'

import { CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { ComponentSettingKey } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompHeadline } from '../../../shared/CompHeadline'
import { CompChartPieProps } from './comp'

export interface CompChartPieWrapperCoreProps {
  infos: CoreComponentInfos
  tableData: CoreComponentTableData
  children: (props: CompChartPieProps) => React.ReactNode
}

/**
 * CompChartPieWrapperCore
 */
export const CompChartPieWrapperCore = ({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex, currentParentBg },
  tableData: { rows = [] },
  children,
}: CompChartPieWrapperCoreProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const idColumn = getSafely(settings, [ComponentSettingKey.PieChartDataIdColumn], '')
  const labelColumn = getSafely(settings, [ComponentSettingKey.PieChartDataLabelColumn], '')
  const valueColumn = getSafely(settings, [ComponentSettingKey.PieChartDataValueColumn], '')

  const itemsPerPage = getSafely(settings, [ComponentSettingKey.PieChartDataItemsPerPage], [10])[0]
  const maxItems = itemsPerPage > 50 ? 50 : itemsPerPage

  const transformedData = (rows || []).slice(0, maxItems).map((row) => {
    return {
      name: row[labelColumn]?.toString() as string,
      value: Math.abs(Number(row[valueColumn]?.value)),
    }
  })

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.PieChartDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.PieChartDesignAlignment] ?? 'parent'
  const margin = settings[ComponentSettingKey.PieChartDesignComponentMargin]

  const colorSequence = getSafely(settings, [ComponentSettingKey.PieChartDesignColorSequence], 'sequential') as
    | 'sequential'
    | 'random'

  const showLegend = getSafely(settings, [ComponentSettingKey.PieChartDesignLegend], true)
  const showLabel = getSafely(settings, [ComponentSettingKey.PieChartDesignLabel], false)
  const isDonut = getSafely(settings, [ComponentSettingKey.PieChartDesignDoughnut], false)

  const typoSetting = settings?.[ComponentSettingKey.PieChartDesignTypography] as TypographyGroupValue

  // ========================= component title / headline =========================
  const headline = settings[ComponentSettingKey.PieChartDataHeading] ?? []
  const headlineAlign = getSafely(settings, [ComponentSettingKey.PieChartDesignHeadingAlignment], 'start')
  const headlineTypography = typoSetting?.[ComponentSettingKey.PieChartDesignHeadingTypography]
  const headlineMargin = settings[ComponentSettingKey.PieChartDesignHeadingMargin]

  // ========================= render =========================
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const compSettings: CompChartPieProps = {
    // base
    id: component._id,
    width,
    alignment,
    margin,
    before: (
      <>
        <CompHeadline
          containerClient={containerClient}
          detailTableId={detailTableId}
          detailRowIndex={detailRowIndex}
          headline={headline}
          compTitleAlign={headlineAlign}
          headlineMargin={headlineMargin}
          headlineTypo={headlineTypography}
          backgroundColor={currentParentBg}
          colorType='adaptive'
        />
      </>
    ),

    // pie-chart
    data: transformedData,
    dataKey: 'value',
    nameKey: 'name',
    backgroundColor: currentParentBg,
    isDonut,
    showLegend,
    showTooltip: true,
    showLabel,
    sequenceType: colorSequence,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(compSettings)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(compSettings)
}
